import { createPortal } from 'react-dom'

import CustomCheckbox from '../../CustomCheckbox'
import { isColumnMenuToggleable } from '../helpers'
import { TableColumn } from '../types'

interface ColumnMenuProps<T> {
  showColumnManagement: boolean
  showColumnMenu: boolean
  columnMenuPosition: { top: number; right: number } | null
  columnsWithRequired: TableColumn<T>[]
  visibleColumns: Set<string>
  columnDropdownRef: React.RefObject<HTMLDivElement>
  toggleColumnVisibilityWrapper: (columnKey: string) => void
}

export function ColumnMenu<T>({
  showColumnManagement,
  showColumnMenu,
  columnMenuPosition,
  columnsWithRequired,
  visibleColumns,
  columnDropdownRef,
  toggleColumnVisibilityWrapper,
}: ColumnMenuProps<T>) {
  if (!showColumnManagement || !showColumnMenu || !columnMenuPosition) return null

  return createPortal(
    <div
      ref={columnDropdownRef}
      className="fixed bg-white border border-gray-200 rounded-md shadow-xl min-w-[200px]"
      role="menu"
      style={{
        outline: 'none',
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        zIndex: 999999,
        top: `${columnMenuPosition.top}px`,
        right: `${columnMenuPosition.right}px`,
        pointerEvents: 'auto',
        opacity: 1,
        backgroundColor: 'white',
      }}
      tabIndex={-1}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation()
          e.preventDefault()
        }
      }}
      onMouseDown={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <div className="p-2">
        <div className="text-xs font-normal text-gray-500 uppercase mb-2 px-2 tracking-wide">
          Show/Hide Columns
        </div>
        {columnsWithRequired.filter(isColumnMenuToggleable).map((column) => {
          const isVisible = visibleColumns.has(column.key)
          return (
            <div
              key={column.key}
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                // CustomCheckbox handles the click via onChange
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  e.preventDefault()
                  if (!column.required) {
                    toggleColumnVisibilityWrapper(column.key)
                  }
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Toggle ${column.label} column visibility`}
            >
              <CustomCheckbox
                checked={isVisible}
                onChange={() => {
                  if (!column.required) {
                    toggleColumnVisibilityWrapper(column.key)
                  }
                }}
                disabled={column.required}
                size="sm"
              />
              <span className={`text-sm font-normal ${column.required ? 'text-gray-400' : 'text-gray-600'}`}>
                {column.label}
                {column.required && <span className="ml-1 text-xs text-gray-400 font-normal">(required)</span>}
              </span>
            </div>
          )
        })}
      </div>
    </div>,
    document.body
  )
}
